import { useEffect, useState } from 'react'
import {
  ArrowLeft,
  Bot,
  ExternalLink,
  GitBranch,
  Loader2,
  Trash2,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useAIAgents } from '@/api/aiAgents'
import { useManagerNavigation } from '@/components/manager/useManagerNavigation'
import { AnswerModeModal } from '@/components/manager/aiAgents/AnswerModeModal'
import { useTeamGroups } from '@/api/teamGroups'
import { QuiRepond, decrireFlux } from './QuiRepond'
import { HeuresDuNumero } from './HeuresDuNumero'
import { cn } from '@/lib/utils'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import type { CompanyPhoneNumber } from '@/api/company'
import {
  formatPhoneNumber,
  useReleasePhoneNumber,
  useUpdatePhoneNumber,
} from '@/api/phoneNumbers'
import { useIvrFlowDetail, useUnassignNumberFromFlow } from '@/api/ivrFlows'
import { SelectFlowDialog } from './SelectFlowDialog'
import { PhoneNumberSettings } from './PhoneNumberSettings'
import { TollfreeSmsSection } from './TollfreeSms'

interface Props {
  number: CompanyPhoneNumber
  companyId: number
  onBack: () => void
  onDeleted: () => void
  onEditFlow?: (flowId: number) => void
  onCreateFlow?: () => void
}

type TabValue = 'answer' | 'general' | 'settings'

/**
 * Page d'un numéro : le centre de l'administration (modèle Quo / Aircall).
 * Tout ce qui concerne ce numéro se règle ici.
 * - Réponse : qui répond (flux IVR, agent IA, équipe), heures (visible par défaut)
 * - Général : nom, type, textos sans frais
 * - Settings : voix, recording, transcription, AI summary (par-numéro)
 * - Danger  : suppression (uniquement si pas main)
 */
export function PhoneNumberDetailView({
  number,
  companyId,
  onBack,
  onDeleted,
  onEditFlow,
  onCreateFlow,
}: Props) {
  const { t } = useTranslation()
  const { locale = 'fr' } = useParams<{ locale: string }>()

  const update = useUpdatePhoneNumber(companyId, number.id)
  const release = useReleasePhoneNumber(companyId)
  const unassignFlow = useUnassignNumberFromFlow()

  const [labelValue, setLabelValue] = useState(number.label ?? '')
  const [editingLabel, setEditingLabel] = useState(false)
  const [showSelectFlow, setShowSelectFlow] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState('')
  const [supprimerOuvert, setSupprimerOuvert] = useState(false)
  const [activeTab, setActiveTab] = useState<TabValue>('answer')
  // Confier le numéro à un agent IA depuis la page du numéro : on choisit
  // l'agent, puis la même modale « quand doit-il répondre ? » que côté agents.
  const { setActiveTab: allerA } = useManagerNavigation()
  const { data: agents = [] } = useAIAgents(companyId)
  const agentsEntrants = agents.filter((a) => a.call_mode !== 'outbound')
  const [agentChoisi, setAgentChoisi] = useState<string>('')
  const [modaleAgent, setModaleAgent] = useState(false)
  // Équipe qui sonne déjà sur ce numéro (premier nœud « Vers équipe » du flux) : on la garde.
  const { data: fluxActuel } = useIvrFlowDetail(number.ivr_flow_id)
  const equipeActuelle = (fluxActuel?.nodes ?? []).find((n) => n.node_type === 'route_team')?.config?.team_id as number | 'all' | undefined
  const { data: equipes = [] } = useTeamGroups()
  // Ce qui se passe vraiment sur ce numéro, lu depuis le flux.
  const etapes = fluxActuel
    ? decrireFlux(fluxActuel, {
        equipe: (id) => equipes.find((e) => String(e.id) === String(id))?.name ?? null,
        agent: (id) => agents.find((a) => String(a.id) === String(id))?.name ?? null,
      }, t)
    : []
  // Un agent IA est déjà branché : directement, ou par un nœud du flux.
  const agentDejaBranche = number.assignment_type === 'ai_agent' || (fluxActuel?.nodes ?? []).some((n) => n.node_type === 'route_ai')

  useEffect(() => {
    setLabelValue(number.label ?? '')
  }, [number.label])

  const effectiveType: CompanyPhoneNumber['assignment_type'] | 'ivr_flow' =
    number.ivr_flow_id ? 'ivr_flow' : number.assignment_type

  const canShowDanger = number.assignment_type !== 'main'

  const confirmWord =
    locale === 'fr' ? t('manager.numbers.detail.confirmWord') : 'delete'
  const canDelete =
    deleteConfirm.toLowerCase() === confirmWord.toLowerCase() &&
    number.assignment_type !== 'main'

  const handleSaveLabel = async () => {
    const trimmed = labelValue.trim()
    if (trimmed === (number.label ?? '')) {
      setEditingLabel(false)
      return
    }
    try {
      await update.mutateAsync({ label: trimmed })
      toast.success(t('manager.numbers.detail.labelSaved'))
      setEditingLabel(false)
    } catch {
      toast.error(t('manager.numbers.detail.labelError'))
    }
  }

  const handleRemoveFlow = async () => {
    if (!number.ivr_flow_id) return
    try {
      await unassignFlow.mutateAsync({
        flowId: number.ivr_flow_id,
        phoneNumberId: number.id,
      })
      toast.success(t('manager.numbers.detail.flowRemoved'))
    } catch {
      toast.error(t('manager.numbers.detail.flowRemoveError'))
    }
  }

  const handleDelete = async () => {
    if (!number.number_sid) {
      toast.error(t('manager.numbers.detail.deleteError'))
      return
    }
    try {
      await release.mutateAsync(number.number_sid)
      toast.success(t('manager.numbers.detail.deleted'))
      onDeleted()
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error || t('manager.numbers.detail.deleteError')
      toast.error(message)
    }
  }

  const purchasedOn = new Date(number.purchased_at).toLocaleDateString(
    locale === 'fr' ? 'fr-CA' : 'en-CA',
    { year: 'numeric', month: 'long', day: 'numeric' },
  )

  return (
    <div className="max-w-[720px] mx-auto py-2">
      {/* Header (fixe pour les 3 tabs) */}
      <div className="mb-8">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center text-sm text-slate-500 hover:text-slate-800 dark:hover:text-slate-300 mb-6 group transition-colors"
        >
          <ArrowLeft className="h-4 w-4 mr-1 group-hover:-translate-x-1 transition-transform" />
          {t('manager.numbers.detail.backToNumbers')}
        </button>

        <div className="flex flex-wrap items-start justify-between gap-4">
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
            {formatPhoneNumber(number.phone_number)}
          </h1>

          <div className="flex gap-2">
            <Capability>{t('manager.numbers.detail.capability.voice')}</Capability>
            {number.is_toll_free && number.tollfree_sms_status !== 'approved' ? (
              <Capability tone="amber">{t(`manager.numbers.tollfreeSms.pill.${number.tollfree_sms_status === 'pending' ? 'pending' : number.tollfree_sms_status === 'rejected' ? 'rejected' : 'none'}`)}</Capability>
            ) : (
              <Capability>SMS</Capability>
            )}
            {number.is_toll_free && (
              <Capability tone="amber">
                {t('manager.numbers.detail.capability.tollFree')}
              </Capability>
            )}
          </div>
        </div>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as TabValue)}
        className="gap-6"
      >
        <TabsList className="w-full">
          <TabsTrigger value="answer">
            {t('manager.numbers.detail.tabAnswer', 'Réponse')}
          </TabsTrigger>
          <TabsTrigger value="general">
            {t('manager.numbers.detail.tabGeneral', 'Général')}
          </TabsTrigger>
          <TabsTrigger value="settings">
            {t('manager.numbers.detail.tabSettings')}
          </TabsTrigger>
        </TabsList>

        {/* === GÉNÉRAL === */}
        <TabsContent value="general" className="space-y-10">
          {/* General Settings */}
          <section>
            <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-6">
              {t('manager.numbers.detail.generalSettings')}
            </h2>

            <div className="space-y-4">
              <div className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-800/50">
                <div>
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    {t('manager.numbers.detail.numberName')}
                  </span>
                  <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
                    {t('manager.numbers.detail.numberNameDesc')}
                  </p>
                </div>
                <div className="relative">
                  <input
                    type="text"
                    value={labelValue}
                    onChange={(e) => {
                      setLabelValue(e.target.value)
                      setEditingLabel(true)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void handleSaveLabel()
                      if (e.key === 'Escape') {
                        setEditingLabel(false)
                        setLabelValue(number.label ?? '')
                      }
                    }}
                    onBlur={() => {
                      if (
                        editingLabel &&
                        labelValue.trim() !== (number.label ?? '')
                      ) {
                        void handleSaveLabel()
                      } else {
                        setEditingLabel(false)
                      }
                    }}
                    placeholder={t('manager.numbers.detail.labelPlaceholder')}
                    className="text-sm text-right text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded px-3 py-1.5 w-52 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 placeholder-slate-400 transition-all"
                  />
                  {update.isPending && (
                    <div className="absolute inset-y-0 right-0 pr-2.5 flex items-center pointer-events-none">
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-800/50">
                <span className="text-sm text-slate-500 dark:text-slate-400">
                  {t('manager.numbers.detail.type')}
                </span>
                <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                  {number.is_toll_free
                    ? t('manager.numbers.detail.typeTollFree')
                    : `${t('manager.numbers.detail.typeLocal')} (Canada)`}
                </span>
              </div>

              <div className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-800/50">
                <span className="text-sm text-slate-500 dark:text-slate-400">
                  {t('manager.numbers.detail.purchaseDate')}
                </span>
                <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                  {purchasedOn}
                </span>
              </div>
            </div>
          </section>

          <TollfreeSmsSection number={number} companyId={companyId} />
        </TabsContent>

        {/* === RÉPONSE : qui répond à ce numéro === */}
        <TabsContent value="answer" className="space-y-8">
          <section>
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-1">
                  {t('manager.numbers.who.title', 'Qui répond')}
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {t('manager.numbers.who.subtitle', "Le parcours d'un appel entrant sur ce numéro.")}
                </p>
              </div>
              {effectiveType === 'ivr_flow' && (
                <span
                  className={cn(
                    'inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium',
                    number.ivr_flow_is_active
                      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400'
                      : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
                  )}
                >
                  <span className={cn('w-1.5 h-1.5 rounded-full mr-1.5', number.ivr_flow_is_active ? 'bg-emerald-500' : 'bg-slate-400')} />
                  {number.ivr_flow_is_active ? t('manager.numbers.detail.flowActive') : t('manager.numbers.detail.flowInactive')}
                </span>
              )}
            </div>

            {effectiveType === 'ivr_flow' && number.ivr_flow_id ? (
              <>
                <QuiRepond etapes={etapes} />
                <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
                  <span className="inline-flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                    <GitBranch className="h-3.5 w-3.5 text-blue-500" />
                    {t('manager.numbers.who.flowName', 'Flux « {{name}} »', { name: number.ivr_flow_name })} · {t('manager.numbers.detail.nodesCount', { count: number.ivr_flow_node_count ?? 0 })}
                  </span>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" onClick={() => number.ivr_flow_id && onEditFlow?.(number.ivr_flow_id)} disabled={!onEditFlow}>
                      {t('manager.numbers.detail.editFlow')}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setShowSelectFlow(true)}>
                      {t('manager.numbers.detail.changeFlow')}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => void handleRemoveFlow()} disabled={unassignFlow.isPending}>
                      {t('manager.numbers.detail.removeFlow')}
                    </Button>
                  </div>
                </div>
              </>
            ) : effectiveType === 'ai_agent' ? (
              <>
                <QuiRepond etapes={[{ quand: t('manager.numbers.who.incoming', 'Appel entrant'), cible: t('manager.numbers.who.agentAlways', '{{name}} (24 h/24)', { name: number.ai_agent_name || number.owner_name || '' }), ia: true }]} />
                <div className="flex flex-wrap gap-2 mt-3 justify-end">
                  <Button size="sm" variant="outline" disabled={!number.ai_agent_id} onClick={() => number.ai_agent_id && allerA('ai-agents', { params: { edit: String(number.ai_agent_id) } })}>
                    <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                    {t('manager.numbers.detail.viewAgent')}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setShowSelectFlow(true)}>
                    {t('manager.numbers.detail.changeFlow')}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <QuiRepond etapes={[{ quand: t('manager.numbers.who.incoming', 'Appel entrant'), cible: t('manager.numbers.who.everyoneDefault', "Toute l'équipe, en même temps") }]} />
                <div className="flex flex-wrap gap-2 mt-3 justify-end">
                  <Button size="sm" onClick={() => setShowSelectFlow(true)}>
                    {t('manager.numbers.detail.changeFlow')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={onCreateFlow} disabled={!onCreateFlow}>
                    {t('manager.numbers.detail.createCustomFlow')}
                  </Button>
                </div>
              </>
            )}
          </section>

          {/* Ajouter un agent IA : seulement quand aucun agent n'est branché sur ce numéro */}
          {!agentDejaBranche && agentsEntrants.length > 0 && (
            <section className="rounded-lg border border-dashed border-purple-200 dark:border-purple-800/60 px-4 py-4">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-1 flex items-center gap-2">
                <Bot className="h-4 w-4 text-purple-600" />
                {t('manager.numbers.who.addAgent', 'Ajouter un agent IA')}
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                {t('manager.numbers.detail.aiAgentDesc')}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <Select value={agentChoisi} onValueChange={setAgentChoisi}>
                  <SelectTrigger className="w-60 h-9">
                    <SelectValue placeholder={t('manager.numbers.detail.pickAgent')} />
                  </SelectTrigger>
                  <SelectContent>
                    {agentsEntrants.map((a) => (
                      <SelectItem key={a.id} value={String(a.id)}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" disabled={!agentChoisi} onClick={() => setModaleAgent(true)}>
                  {t('manager.numbers.detail.assignAgent')}
                </Button>
              </div>
            </section>
          )}

          {/* Heures d'ouverture : celles de l'entreprise par défaut, ou propres au numéro */}
          <HeuresDuNumero companyId={companyId} numberId={number.id} onVoirEntreprise={() => allerA('schedule')} />
        </TabsContent>

        {/* === SETTINGS (la suppression du numéro vit ici, en bas, comme chez Quo) === */}
        <TabsContent value="settings" className="pt-2 space-y-6">
          <PhoneNumberSettings number={number} companyId={companyId} />
          {canShowDanger && (
            <section className="rounded-lg border border-red-200 dark:border-red-900/40 px-4 py-3">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-red-700 dark:text-red-400">
                    {t('manager.numbers.detail.deleteNumber')}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    {t('manager.numbers.detail.deleteWarning')}
                  </p>
                </div>
                {!supprimerOuvert && (
                  <Button type="button" size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400" onClick={() => setSupprimerOuvert(true)}>
                    <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                    {t('manager.numbers.detail.deleteButton')}
                  </Button>
                )}
              </div>
              {supprimerOuvert && (
                <div className="mt-3 pt-3 border-t border-red-100 dark:border-red-900/30 flex flex-wrap items-center gap-2">
                  <span className="text-xs text-slate-600 dark:text-slate-300">
                    {t('manager.numbers.detail.confirmInstruction')} <strong>{confirmWord}</strong> {t('manager.numbers.detail.confirmInstructionEnd')}
                  </span>
                  <input
                    type="text"
                    value={deleteConfirm}
                    onChange={(e) => setDeleteConfirm(e.target.value)}
                    placeholder={confirmWord}
                    className="h-9 w-40 px-3 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  />
                  <button
                    type="button"
                    onClick={() => void handleDelete()}
                    disabled={!canDelete || release.isPending}
                    className={cn(
                      'inline-flex items-center gap-1.5 h-9 bg-red-600 text-white px-3 rounded text-sm font-medium transition-colors',
                      canDelete && !release.isPending ? 'hover:bg-red-700' : 'opacity-50 cursor-not-allowed',
                    )}
                  >
                    {release.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                    {t('manager.numbers.detail.deleteButton')}
                  </button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => { setSupprimerOuvert(false); setDeleteConfirm('') }}>
                    {t('common.cancel')}
                  </Button>
                </div>
              )}
            </section>
          )}
        </TabsContent>
      </Tabs>

      {modaleAgent && agentChoisi && (
        <AnswerModeModal
          isOpen={modaleAgent}
          onClose={() => setModaleAgent(false)}
          companyId={companyId}
          agentId={Number(agentChoisi)}
          agentName={agentsEntrants.find((a) => String(a.id) === agentChoisi)?.name ?? ''}
          lockedPhoneNumberId={number.id}
          currentTeamId={equipeActuelle ?? null}
        />
      )}

      <SelectFlowDialog
        open={showSelectFlow}
        onClose={() => setShowSelectFlow(false)}
        phoneNumberId={number.id}
        currentFlowId={number.ivr_flow_id}
      />
    </div>
  )
}

function Capability({
  children,
  tone = 'slate',
}: {
  children: React.ReactNode
  tone?: 'slate' | 'amber'
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center px-3 py-1 rounded-full text-xs font-medium',
        tone === 'amber'
          ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300'
          : 'bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200',
      )}
    >
      {children}
    </span>
  )
}
